import { useState, useEffect, useRef, useCallback } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Users, Clock, CheckCircle2, Radio, RefreshCw, Activity } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

function createPulsingIcon(color) {
  return L.divIcon({
    className: '',
    html: `<div style="position:relative;width:32px;height:32px;">
      <div style="position:absolute;width:32px;height:32px;border-radius:50%;background:${color};opacity:0.3;animation:pulse-ring 1.5s ease-out infinite;"></div>
      <div style="position:absolute;top:6px;left:6px;width:20px;height:20px;border-radius:50%;background:${color};border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);"></div>
    </div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
    popupAnchor: [0, -16],
  });
}

export default function LiveTracking() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const intervalRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API_URL}/api/shifts/live-tracking`, { withCredentials: true });
      setData(d);
      setLastUpdated(new Date());
      updateMap(d.active_shifts);
    } catch (err) { console.error('Live tracking fetch failed', err); }
    setLoading(false);
  }, []);

  const updateMap = (shifts) => {
    if (!mapInstanceRef.current) return;
    // Clear old markers
    markersRef.current.forEach(m => mapInstanceRef.current.removeLayer(m));
    markersRef.current = [];

    if (shifts.length === 0) return;

    const bounds = [];
    shifts.forEach(s => {
      const icon = createPulsingIcon('#00A6A0');
      const marker = L.marker([s.latitude, s.longitude], { icon }).addTo(mapInstanceRef.current);
      const dur = s.duration_minutes;
      const durStr = dur >= 60 ? `${Math.floor(dur / 60)}h ${dur % 60}m` : `${dur}m`;
      marker.bindPopup(`
        <div style="min-width:180px;font-family:system-ui,sans-serif;">
          <p style="font-weight:600;margin:0 0 4px;font-size:14px;">${s.caregiver_name}</p>
          ${s.client_name ? `<p style="color:#666;margin:0 0 4px;font-size:12px;">Client: ${s.client_name}</p>` : ''}
          <p style="color:#666;margin:0 0 2px;font-size:11px;">Clocked in: ${s.check_in ? new Date(s.check_in).toLocaleTimeString() : 'N/A'}</p>
          <p style="color:#666;margin:0 0 2px;font-size:11px;">Duration: ${durStr}</p>
          <p style="color:#666;margin:0;font-size:11px;">Shift: ${s.start_time || ''} - ${s.end_time || ''}</p>
          ${s.address ? `<p style="color:#888;margin:4px 0 0;font-size:10px;">${s.address}</p>` : ''}
        </div>
      `);
      marker.on('click', () => setSelectedShift(s));
      markersRef.current.push(marker);
      bounds.push([s.latitude, s.longitude]);

      // Add accuracy circle
      const circle = L.circle([s.latitude, s.longitude], {
        radius: 100, color: '#00A6A0', fillColor: '#00A6A0',
        fillOpacity: 0.08, weight: 1, dashArray: '4 4',
      }).addTo(mapInstanceRef.current);
      markersRef.current.push(circle);
    });

    if (bounds.length > 0) {
      mapInstanceRef.current.fitBounds(bounds, { padding: [60, 60], maxZoom: 14 });
    }
  };

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    const map = L.map(mapRef.current, { zoomControl: false }).setView([51.505, -0.09], 6);
    L.control.zoom({ position: 'bottomright' }).addTo(map);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap',
      maxZoom: 19,
    }).addTo(map);
    mapInstanceRef.current = map;
    setTimeout(() => map.invalidateSize(), 200);
  }, []);

  useEffect(() => {
    fetchData();
    intervalRef.current = setInterval(fetchData, 30000); // Poll every 30s
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [fetchData]);

  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  const stats = data?.stats || { active_now: 0, assigned_pending: 0, completed_today: 0 };
  const activeShifts = data?.active_shifts || [];

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="live-tracking-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-[#00A6A0]/10">
              <Radio className="w-6 h-6 text-[#00A6A0]" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground">Live Tracking</h1>
              <p className="text-muted-foreground text-sm">
                Real-time caregiver location monitoring
                {lastUpdated && <span className="ml-2 text-xs">· Updated {lastUpdated.toLocaleTimeString()}</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00A6A0]/10 text-[#00A6A0] text-xs font-medium">
              <span className="w-2 h-2 rounded-full bg-[#00A6A0] animate-pulse" />
              Live
            </div>
            <Button variant="outline" size="sm" className="border-border text-foreground" onClick={fetchData} data-testid="refresh-tracking-btn">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="glass-card border-border" data-testid="stat-active-now">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-[#00A6A0]/10">
                <Activity className="w-5 h-5 text-[#00A6A0]" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Active Now</p>
                <p className="text-2xl font-bold text-[#00A6A0]">{stats.active_now}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card border-border" data-testid="stat-assigned">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Clock className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Pending Shifts</p>
                <p className="text-2xl font-bold text-amber-500">{stats.assigned_pending}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card border-border" data-testid="stat-completed">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Completed Today</p>
                <p className="text-2xl font-bold text-emerald-500">{stats.completed_today}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Map + Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4" style={{ minHeight: '500px' }}>
          {/* Map */}
          <div className="lg:col-span-3 relative">
            <div
              ref={mapRef}
              className="w-full rounded-xl border border-border overflow-hidden"
              style={{ height: '500px' }}
              data-testid="live-tracking-map"
            />
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/50 rounded-xl">
                <div className="spinner w-8 h-8" />
              </div>
            )}
            {!loading && activeShifts.length === 0 && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-background/80 rounded-xl">
                <MapPin className="w-16 h-16 text-muted-foreground/20 mb-3" />
                <p className="text-muted-foreground font-medium">No active shifts right now</p>
                <p className="text-xs text-muted-foreground mt-1">Caregivers will appear here when they clock in</p>
              </div>
            )}
            {/* Pulse animation style */}
            <style>{`@keyframes pulse-ring { 0% { transform: scale(0.8); opacity: 0.5; } 100% { transform: scale(2.2); opacity: 0; } }`}</style>
          </div>

          {/* Active Caregivers List */}
          <div className="lg:col-span-1">
            <Card className="glass-card border-border h-full" data-testid="active-caregivers-list">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-4 h-4 text-[#00A6A0]" />
                  <p className="text-sm font-semibold text-foreground">Active Caregivers</p>
                  <span className="ml-auto text-xs bg-[#00A6A0]/10 text-[#00A6A0] px-2 py-0.5 rounded-full font-medium">{activeShifts.length}</span>
                </div>
                {activeShifts.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">No caregivers clocked in</p>
                ) : (
                  <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                    {activeShifts.map((s, i) => {
                      const dur = s.duration_minutes;
                      const durStr = dur >= 60 ? `${Math.floor(dur / 60)}h ${dur % 60}m` : `${dur}m`;
                      const isSelected = selectedShift?.id === s.id;
                      return (
                        <div
                          key={s.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            isSelected ? 'border-[#00A6A0] bg-[#00A6A0]/5' : 'border-border/50 bg-muted/20 hover:bg-muted/40'
                          }`}
                          onClick={() => {
                            setSelectedShift(s);
                            if (mapInstanceRef.current) {
                              mapInstanceRef.current.setView([s.latitude, s.longitude], 16, { animate: true });
                              markersRef.current.forEach(m => {
                                if (m instanceof L.Marker && m.getLatLng().lat === s.latitude && m.getLatLng().lng === s.longitude) {
                                  m.openPopup();
                                }
                              });
                            }
                          }}
                          data-testid={`active-caregiver-${i}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="w-2 h-2 rounded-full bg-[#00A6A0] animate-pulse flex-shrink-0" />
                            <p className="text-sm font-medium text-foreground truncate">{s.caregiver_name}</p>
                          </div>
                          {s.client_name && (
                            <p className="text-xs text-muted-foreground truncate pl-4">Client: {s.client_name}</p>
                          )}
                          <div className="flex items-center justify-between pl-4 mt-1">
                            <span className="text-xs text-muted-foreground">{s.start_time} - {s.end_time}</span>
                            <span className="text-xs font-medium text-[#00A6A0]">{durStr}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
